import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import axios from "axios";
import { Menu, X, ChevronRight, Phone, Mail, MapPin, Instagram, Facebook, Clock, Star, GraduationCap, Sparkles, Award, Users, Heart, ArrowRight, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// LOGO
const LOGO_URL = "https://customer-assets.emergentagent.com/job_nonu-aesthetic/artifacts/001zr5og_Untitled%20design%20-%202026-03-24T112655.972.png";

// PROPER TREATMENT IMAGES (from image search - matching reference aesthetic)
const IMAGES = {
  hero: "https://images.pexels.com/photos/3985338/pexels-photo-3985338.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260",
  facial: "https://images.pexels.com/photos/3985331/pexels-photo-3985331.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  injectables: "https://images.pexels.com/photos/3985329/pexels-photo-3985329.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  laser: "https://images.pexels.com/photos/4586728/pexels-photo-4586728.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  laserAlt: "https://images.pexels.com/photos/4586726/pexels-photo-4586726.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  bodyContouring: "https://images.pexels.com/photos/5069432/pexels-photo-5069432.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  wellness: "https://images.pexels.com/photos/3757952/pexels-photo-3757952.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  goldFacial: "https://images.pexels.com/photos/3212179/pexels-photo-3212179.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  spaInterior: "https://images.pexels.com/photos/6620856/pexels-photo-6620856.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  treatment1: "https://images.pexels.com/photos/3997381/pexels-photo-3997381.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  treatment2: "https://images.pexels.com/photos/3997382/pexels-photo-3997382.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  skincare: "https://images.pexels.com/photos/3762879/pexels-photo-3762879.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

// Logo Component - BIGGER
const Logo = ({ className = "" }) => (
  <img 
    src={LOGO_URL}
    alt="NONU Aesthetic & Wellness"
    className={`h-20 md:h-24 w-auto ${className}`}
    width="96"
    height="96"
  />
);

// Intersection Observer Hook for Animations
const useIntersectionObserver = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.1, ...options });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ref, isVisible];
};

// Animated Section Component
const AnimatedSection = ({ children, className = "", animation = "fade-in-up", delay = 0 }) => {
  const [ref, isVisible] = useIntersectionObserver();
  
  const animationClass = {
    'fade-in-up': 'animate-fade-in-up',
    'fade-in': 'animate-fade-in',
    'slide-left': 'animate-slide-left',
    'slide-right': 'animate-slide-right',
    'scale-in': 'animate-scale-in',
  }[animation];

  return (
    <div 
      ref={ref} 
      className={`${className} ${isVisible ? animationClass : 'opacity-0'}`}
      style={{ animationDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { path: "/treatments", label: "Treatments" },
    { path: "/membership", label: "Membership" },
    { path: "/academy", label: "Academy" },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
  ];

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'glass-nav py-2' : 'bg-[#0a0a0a] py-4'
      }`}
    >
      <div className="container-max px-4 md:px-8 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="relative z-10 -my-2">
          <Logo />
        </Link>
        
        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.label.toLowerCase()}`}
              className={`nav-link ${location.pathname === link.path ? 'text-[#c9a050]' : ''}`}
            >
              {link.label}
            </Link>
          ))}
          <Link 
            to="/book" 
            data-testid="nav-book-now"
            className="btn-gold"
          >
            Book Now
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button 
          data-testid="mobile-menu-toggle"
          className="lg:hidden relative z-10 text-[#f8f4ef]"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>

        {/* Mobile Navigation */}
        <div 
          className={`fixed inset-0 bg-[#0a0a0a] transition-transform duration-500 lg:hidden ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex flex-col items-center justify-center h-full gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                className="font-display text-3xl text-[#f8f4ef] hover:text-[#c9a050] transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <Link 
              to="/book" 
              data-testid="mobile-nav-book"
              className="btn-gold mt-4"
            >
              Book Now
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-[#2d261f] text-[#f8f4ef]">
      <div className="container-max section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Logo className="mb-6 h-16" />
            <p className="text-[#a89b8a] text-sm leading-relaxed mb-6">
              Refined Beauty. Elevated Confidence. A luxury aesthetic & wellness house dedicated to transformative results and elite experiences.
            </p>
            <div className="flex gap-4">
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" data-testid="social-instagram" className="w-10 h-10 rounded-full bg-[#c9a050]/20 flex items-center justify-center text-[#c9a050] hover:bg-[#c9a050] hover:text-white transition-all" aria-label="Instagram">
                <Instagram size={18} />
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" data-testid="social-facebook" className="w-10 h-10 rounded-full bg-[#c9a050]/20 flex items-center justify-center text-[#c9a050] hover:bg-[#c9a050] hover:text-white transition-all" aria-label="Facebook">
                <Facebook size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-lg text-[#c9a050] mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {["Treatments", "Membership", "Academy", "About", "Contact"].map((item) => (
                <li key={item}>
                  <Link to={`/${item.toLowerCase()}`} className="text-[#a89b8a] hover:text-[#c9a050] text-sm transition-colors flex items-center gap-2">
                    <ChevronRight size={12} className="text-[#c9a050]" />{item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-display text-lg text-[#c9a050] mb-6">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-[#a89b8a]">
                <MapPin size={16} className="text-[#c9a050] mt-1 flex-shrink-0" />
                <span>801 S. Bowman, Suite 4<br />Little Rock, AR 72201</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-[#a89b8a]">
                <Phone size={16} className="text-[#c9a050] flex-shrink-0" />
                <a href="tel:+15015543307" className="hover:text-[#c9a050] transition-colors">(501) 554-3307</a>
              </li>
              <li className="flex items-center gap-3 text-sm text-[#a89b8a]">
                <Mail size={16} className="text-[#c9a050] flex-shrink-0" />
                <a href="mailto:hello@nonuaesthetics.com" className="hover:text-[#c9a050] transition-colors">hello@nonuaesthetics.com</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-[#c9a050]/20 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#7a6b5a] text-xs">
            &copy; {new Date().getFullYear()} NONU Aesthetic & Wellness. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-[#7a6b5a] text-xs hover:text-[#c9a050] transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="text-[#7a6b5a] text-xs hover:text-[#c9a050] transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const Home = () => {
  const services = [
    { title: "Injectables", items: ["Botox & Dysport", "Dermal Fillers", "Lip Enhancement", "Facial Contouring"], image: IMAGES.injectables, link: "/treatments#injectables" },
    { title: "Skin Rejuvenation", items: ["Microneedling", "Chemical Peels", "PRP Facial", "Medical Grade Facials"], image: "https://customer-assets.emergentagent.com/job_6cb645ec-54ef-4f51-8b57-1a05f31f5690/artifacts/fwj371cz_cae612a3-d17e-42b7-9768-0a457abac818.jpg", link: "/treatments#face" },
    { title: "Laser Treatments", items: ["Laser Hair Removal", "Skin Resurfacing", "Pigmentation Correction", "Tattoo Removal"], image: IMAGES.laser, link: "/treatments#laser" },
    { title: "Body Contouring", items: ["Non-Surgical Fat Reduction", "Body Sculpting", "Skin Tightening", "Exclusive Events"], image: IMAGES.bodyContouring, link: "/treatments#wellness" },
  ];

  const features = [
    "Cutting-Edge Technology",
    "Advanced Expertise", 
    "Luxury Experience",
    "Personalized Care",
    "Natural-Looking Results",
  ];

  return (
    <main data-testid="home-page" className="bg-[#f8f4ef]">
      {/* HERO SECTION - Full Image Visible */}
      <section className="relative overflow-hidden pt-24 md:pt-28" data-testid="hero-section">
        {/* Hero Background Image - Full Visible */}
        <div className="relative w-full">
          <img 
            src="https://customer-assets.emergentagent.com/job_nonu-aesthetic/artifacts/2yxwwy6c_ac4b8017-18fc-4b19-86cb-ce1546f380ec.png"
            alt="NONU Luxury Reception"
            className="w-full h-auto min-h-[500px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/85 via-[#0a0a0a]/50 to-transparent" />
        </div>
        
        {/* Hero Content - Far Left Overlay */}
        <div className="absolute inset-0 z-10 flex items-center pt-24 md:pt-28">
          <div className="pl-6 md:pl-12 lg:pl-20">
            <div className="max-w-md">
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#f8f4ef] mb-6 leading-tight animate-fade-in-up italic">
                The Art of Refined Aesthetics
              </h1>
              <p className="text-[#c9a050] text-sm mb-8 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                Refined Beauty. Elevated Confidence.
              </p>
              
              {/* Feature List - Clickable */}
              <ul className="space-y-2 mb-8 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                <li className="group">
                  <Link to="/treatments#injectables" className="flex items-center gap-3 text-[#f8f4ef] hover:text-[#c9a050] transition-colors">
                    <span className="w-2 h-2 rounded-full bg-[#c9a050] group-hover:scale-125 transition-transform" />
                    <span className="text-sm">Cutting-Edge Technology</span>
                    <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
                <li className="group">
                  <Link to="/about" className="flex items-center gap-3 text-[#f8f4ef] hover:text-[#c9a050] transition-colors">
                    <span className="w-2 h-2 rounded-full bg-[#c9a050] group-hover:scale-125 transition-transform" />
                    <span className="text-sm">Advanced Expertise</span>
                    <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
                <li className="group">
                  <Link to="/membership" className="flex items-center gap-3 text-[#f8f4ef] hover:text-[#c9a050] transition-colors">
                    <span className="w-2 h-2 rounded-full bg-[#c9a050] group-hover:scale-125 transition-transform" />
                    <span className="text-sm">Luxury Experience</span>
                    <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
                <li className="group">
                  <Link to="/treatments" className="flex items-center gap-3 text-[#f8f4ef] hover:text-[#c9a050] transition-colors">
                    <span className="w-2 h-2 rounded-full bg-[#c9a050] group-hover:scale-125 transition-transform" />
                    <span className="text-sm">Personalized Care</span>
                    <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
                <li className="group">
                  <Link to="/treatments#face" className="flex items-center gap-3 text-[#f8f4ef] hover:text-[#c9a050] transition-colors">
                    <span className="w-2 h-2 rounded-full bg-[#c9a050] group-hover:scale-125 transition-transform" />
                    <span className="text-sm">Natural-Looking Results</span>
                    <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              </ul>

              <div className="flex flex-wrap gap-4 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
                <Link 
                  to="/book" 
                  data-testid="hero-book-consultation" 
                  className="btn-gold inline-block animate-pulse-glow"
                >
                  Book Consultation <ChevronRight size={16} className="inline ml-1" />
                </Link>
                <Link 
                  to="/treatments" 
                  data-testid="hero-view-treatments" 
                  className="btn-gold-outline inline-block text-[#f8f4ef] border-[#f8f4ef]/50 hover:border-[#c9a050] hover:text-[#c9a050]"
                >
                  View Treatments
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid - More Spacing Between Sections */}
      <section className="section-padding section-cream-light" data-testid="services-section">
        <div className="container-max">
          <AnimatedSection className="text-center mb-16">
            <h2 className="font-display text-4xl md:text-5xl text-[#2d261f] mb-4 italic">Advanced Aesthetic Treatments</h2>
            <p className="text-[#7a6b5a] max-w-2xl mx-auto">
              Experience the latest in aesthetic medicine and start your journey to refined beauty with our curated selection of advanced treatments.
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#c9a050] to-transparent mx-auto mt-6" />
          </AnimatedSection>
          
          <div className="space-y-20">
            {services.map((service, index) => (
              <AnimatedSection 
                key={service.title} 
                animation={index % 2 === 0 ? "slide-left" : "slide-right"} 
                delay={index * 0.1}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <h3 className="font-display text-3xl text-[#2d261f] mb-6 italic">{service.title}</h3>
                  <ul className="space-y-3 mb-8">
                    {service.items.map((item, idx) => (
                      <li key={item} className="flex items-center gap-3 text-[#7a6b5a]">
                        <span className="w-2 h-2 rounded-full bg-[#c9a050]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <Link to={service.link} className="btn-gold" data-testid={`explore-${service.title.toLowerCase().replace(/\s+/g, '-')}`}>
                    Explore {service.title} <ChevronRight size={14} className="inline ml-1" />
                  </Link>
                </div>
                <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="img-zoom rounded-xl overflow-hidden shadow-2xl border-4 border-[#c9a050]/20">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-80 object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-[#c9a050] rounded-xl -z-10" />
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose NONU - The Art of Refined Aesthetics */}
      <section className="section-padding section-cream" data-testid="why-choose-section">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection animation="slide-left">
              <h2 className="font-display text-4xl md:text-5xl text-[#2d261f] mb-6 italic">
                The Art of Refined Aesthetics
              </h2>
              <p className="text-[#7a6b5a] leading-relaxed mb-8">
                At NONU, we believe in the perfect fusion of medical precision and artistic vision. Our team of elite practitioners delivers transformative results in an environment designed for your comfort and luxury.
              </p>
              <ul className="space-y-4 mb-8">
                {features.map((feature, idx) => (
                  <li key={feature} className="flex items-center gap-4">
                    <span className="w-10 h-10 rounded-full bg-[#c9a050]/10 border border-[#c9a050]/30 flex items-center justify-center text-[#c9a050]">
                      <Sparkles className="w-5 h-5" />
                    </span>
                    <span className="text-[#2d261f] font-medium">{feature}</span>
                  </li>
                ))}
              </ul>
              <Link to="/about" data-testid="why-choose-nonu" className="btn-gold">
                Why Choose NONU <ChevronRight size={16} className="inline ml-1" />
              </Link>
            </AnimatedSection>
            
            <AnimatedSection animation="slide-right" className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-[#c9a050]/20">
                <img 
                  src={IMAGES.spaInterior}
                  alt="NONU Spa Interior"
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
              {/* Gold accent frame */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 border-2 border-[#c9a050] rounded-2xl -z-10" />
              <div className="absolute -top-4 -left-4 w-20 h-20 bg-[#c9a050]/10 rounded-full -z-10" />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Membership Preview - NONU Elite Membership */}
      <section className="relative overflow-hidden" data-testid="membership-preview-section">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left - Dark section with membership info */}
          <div className="bg-[#2d261f] section-padding flex items-center">
            <AnimatedSection animation="slide-left" className="max-w-lg mx-auto lg:ml-auto lg:mr-12">
              <h2 className="font-display text-3xl md:text-4xl text-[#f8f4ef] mb-4">
                Membership
              </h2>
              <p className="text-[#c9a050] mb-6">4 Tiers &mdash; From $149 to $2,500/month</p>
              <ul className="space-y-3 mb-8">
                {["Monthly Facials & Treatments Included", "Up to 20% Off All Services", "Injectable Banks & Treatment Credits", "Same-Week Priority Booking"].map((perk) => (
                  <li key={perk} className="flex items-center gap-3 text-[#a89b8a]">
                    <Heart size={16} className="text-[#c9a050]" />
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>
              <Link to="/membership" data-testid="explore-membership" className="btn-gold">
                Explore Membership <ArrowRight size={16} className="inline ml-1" />
              </Link>
            </AnimatedSection>
          </div>
          
          {/* Right - Image */}
          <div className="h-96 lg:h-auto relative">
            <img 
              src={IMAGES.goldFacial}
              alt="Gold Facial Treatment"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Academy Preview */}
      <section className="section-padding section-cream-light" data-testid="academy-preview-section">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection animation="slide-left">
              <p className="text-[#c9a050] text-xs tracking-[0.25em] uppercase mb-4 font-semibold">Professional Training</p>
              <h2 className="font-display text-4xl md:text-5xl text-[#2d261f] mb-6 italic">
                BBI &mdash; The School of Advancement
              </h2>
              <p className="text-[#7a6b5a] leading-relaxed mb-6">
                4&ndash;6 week accelerated certification in medical aesthetics. Laser, injectables, skin correction, and business training.
              </p>
              <ul className="space-y-3 mb-8">
                {["Laser & Device Certification", "Injectables Theory + Observation", "Microneedling + Skin Correction", "Med Spa Business & Wealth Training"].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-[#2d261f]">
                    <GraduationCap size={16} className="text-[#c9a050]" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/academy" data-testid="explore-academy" className="btn-gold">
                Explore Programs <ArrowRight size={16} className="inline ml-1" />
              </Link>
            </AnimatedSection>
            <AnimatedSection animation="slide-right">
              <div className="bg-[#2d261f] p-6 rounded-2xl shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_e6befdac-fb4e-45a5-9c8e-99e7782923fc/artifacts/at3d9riy_8a2de9fb-6ff3-41aa-a0d5-e4fdc144b778.jpg"
                  alt="BBI Training Academy"
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding section-cream" data-testid="faq-section">
        <div className="container-max">
          <AnimatedSection className="text-center mb-12">
            <p className="text-[#c9a050] text-xs tracking-[0.25em] uppercase mb-4 font-semibold">Common Questions</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2d261f] mb-4 italic">Frequently Asked Questions</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#c9a050] to-transparent mx-auto" />
          </AnimatedSection>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                question: "What should I expect during my first consultation?",
                answer: "Your first consultation is a comprehensive assessment where our expert practitioners will discuss your aesthetic goals, review your medical history, and create a personalized treatment plan tailored to your needs."
              },
              {
                question: "How long do injectable treatments last?",
                answer: "Results vary by treatment type. Botox typically lasts 3-4 months, while dermal fillers can last 6-18 months depending on the product used and treatment area."
              },
              {
                question: "Is there any downtime after treatments?",
                answer: "Most of our non-invasive treatments require minimal to no downtime. We'll provide specific aftercare instructions based on your treatment plan."
              },
              {
                question: "What are the benefits of becoming a NONU member?",
                answer: "NONU members enjoy exclusive benefits including monthly treatments, VIP pricing (up to 30% off), priority booking, complimentary add-ons, and access to member-only events."
              },
              {
                question: "Do you offer financing options?",
                answer: "Yes, we offer flexible financing options to make your aesthetic journey more accessible. Ask our team about our payment plans during your consultation."
              }
            ].map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding section-cream-light text-center" data-testid="cta-section">
        <AnimatedSection className="container-max">
          <div className="gold-line mx-auto mb-6" />
          <h2 className="font-display text-4xl md:text-5xl text-[#2d261f] mb-6 italic">
            Begin Your Transformation
          </h2>
          <p className="text-[#7a6b5a] max-w-xl mx-auto mb-10">
            Schedule your complimentary consultation and discover what's possible with NONU.
          </p>
          <Link to="/book" data-testid="cta-book-now" className="btn-gold">
            Book Your Consultation
          </Link>
        </AnimatedSection>
      </section>
    </main>
  );
};

// Service Menu Data
const SERVICE_MENU = [
  {
    id: "consultations", name: "Consultations",
    intro: "Begin your aesthetic journey with a personalized consultation. Our expert team will assess your needs and create a customized treatment plan.",
    note: "NO REFUNDS FOR VIRTUAL OR IN-PERSON CONSULTATIONS ONCE APPOINTMENT IS SCHEDULED. Please have questions ready — consultations are 30 minutes.",
    services: [
      { name: "In-Person Consultation", desc: "Meet with our team face-to-face for a comprehensive skin assessment and personalized treatment plan.", prices: [{ l: "30 Minutes", p: "$75" }] },
      { name: "Virtual Consultation", desc: "Connect with our experts from the comfort of your home via video call.", prices: [{ l: "30 Minutes", p: "$75" }] },
    ],
  },
  {
    id: "facials", name: "Facials",
    intro: "Each facial is customized to meet your skin's needs. Our estheticians will analyze your skin and create the perfect facial. For best results we recommend a minimum of three treatments with a personalized home-care system.",
    note: "Services with an * do not include extractions due to time limitations or specific techniques.",
    services: [
      { name: "NONU Facial Treatments", desc: "Customized for dry, oily, aging, uneven, sensitive and acneic skin. Designed to balance your skin and reveal its natural beauty.", prices: [{ l: "30 Minutes*", p: "$68" }, { l: "60 Minutes", p: "$98" }] },
      { name: "NONU Stress-Relief Facial*", desc: "Head-to-toe experience for total body relaxation and skin rejuvenation. Combines aroma with facial techniques and the Aveda Perfecting Plant Peel. Includes shoulders, neck, upper back, hand, foot and scalp massage.", prices: [{ l: "75 Minutes", p: "$145" }] },
      { name: "Botanical Hydration Facial*", desc: "Restore optimal moisture levels with cactus fruit stem cells and plant-derived hyaluronic acid. Immediate doubling of skin's hydration.", prices: [{ l: "60 Minutes", p: "$120" }] },
      { name: "NONU Red Reducing Facial", desc: "High-frequency electrode light energy kills acne-causing bacteria. Includes extractions, LED-translucent hydro jelly mask, and multi-wave LED therapy.", prices: [{ l: "75 Minutes", p: "$175" }] },
      { name: "NONU PEEL Facial*", desc: "Plant-powered thermal peel that retexturizes skin, smooths lines and wrinkles, evens skin tone — instant radiance of a 30% glycolic peel without redness.", prices: [{ l: "60 Minutes", p: "$150" }, { l: "Add-on Service", p: "$27" }] },
      { name: "NONU Teen Facial", desc: "Deep cleansing acne treatment for adolescent skin. Gentle extractions, customized mask. Ages 15 and under. Parental Consent Form required.", prices: [{ l: "45 Minutes", p: "$85" }] },
      { name: "NONU Korean Facial", desc: "NuPeel non-acid exfoliant safe for all skin types. Includes cleanse, exfoliation, hydrating mask, serum, moisturizer & SPF, and neck massage.", prices: [{ l: "30 Minutes", p: "$140" }] },
      { name: "NONU Collagen Facial", desc: "Supports natural collagen production with Aveda's Advanced Botanical Kinetics line with phytotech vegan collagen. Deep cleansing, gentle exfoliation, targeted massage.", prices: [{ l: "60 Minutes", p: "$125" }] },
    ],
  },
  {
    id: "hydrafacials", name: "Hydrafacials",
    services: [
      { name: "Glide & Glow Hydrafacial\u2122", desc: "Deeply cleanse, exfoliate, and extract impurities while infusing nourishing hydration. Finished with Aveda moisturizer, eye cream, and aroma ritual.", prices: [{ l: "Treatment", p: "$185" }, { l: "Series of 3", p: "$495" }] },
      { name: "Illuminating Luxe Hydrafacial\u2122", desc: "Everything in Glide & Glow plus personalized booster and LED Light Therapy to combat acne or reduce signs of aging.", prices: [{ l: "Treatment", p: "$235" }, { l: "Series of 3", p: "$640" }] },
      { name: "Luminous Transforming Hydrafacial\u2122", desc: "Everything in Illuminating Luxe plus relaxing lymphatic therapy for face firming and contouring.", prices: [{ l: "Treatment", p: "$285" }, { l: "Series of 3", p: "$780" }] },
      { name: "Ultra Radiant Hydrafacial", desc: "Glide & Glow plus dermaplaning, LED treatment, personalized booster, extractions, and hydrojelly mask.", prices: [{ l: "Treatment", p: "$290" }, { l: "Series of 3", p: "$786" }] },
      { name: "Bride to Be Hydrafacial", desc: "The ultimate pre-wedding glow. Glide & Glow, dermaplaning, extractions, LED therapy, hydrojelly mask, booster, plus hand, neck, shoulder, and scalp massage.", prices: [{ l: "Treatment", p: "$325" }] },
      { heading: "Hydrafacial Add-Ons" },
      { name: "Lip or Eye Perk", desc: "Exfoliate, hydrate, plump lips or brighten eye area. Includes take-home serum.", prices: [{ p: "$50" }] },
      { name: "Lymphatic Treatment", desc: "Face firming and contouring.", prices: [{ p: "$77" }] },
      { name: "Neck & Decollete Treatment", prices: [{ p: "$130" }] },
      { name: "Neck Treatment", prices: [{ p: "$55" }] },
      { name: "Dermaplaning Add-on", prices: [{ p: "$75" }] },
      { name: "Hand Treatment", prices: [{ p: "$55" }] },
    ],
  },
  {
    id: "advanced", name: "Peels & Advanced",
    services: [
      { name: "Dermaplaning & Enzyme Facial", desc: "Removes dead skin cells and peach fuzz with no downtime. Includes NuPeel fruit enzyme for exfoliating and evening skin tone.", prices: [{ l: "60 Minutes", p: "$145" }] },
      { name: "NONU Dermaplaning Trifecta", desc: "Three-part exfoliation: sterile blade, NuPeel fruit enzyme, and Happy Peel (lactic acid, salicylic acid, mandelic acid, retinol).", prices: [{ l: "60 Minutes", p: "$165" }] },
      { name: "NONU Microdermabrasion", desc: "Non-surgical removal of dead skin. Reduces fine lines, wrinkles, age spots, acne scars. Stimulates collagen and elastin production.", prices: [{ l: "30 Minutes*", p: "$80" }, { l: "60 Minute Facial", p: "$150" }] },
      { name: "NONU Mandelic Peel", desc: "Deeply cleanses, exfoliates and treats skin. Recommended for oily, congested and acne-prone skin.", prices: [{ l: "45 Minutes", p: "$135" }, { l: "Series of 3", p: "$360" }] },
      { name: "NONU PHA Peel", desc: "Gently exfoliates, brightens, and hydrates while supporting the skin barrier. Perfect for sensitive or reactive skin.", prices: [{ l: "45 Minutes", p: "$127" }, { l: "Series of 3", p: "$345" }] },
      { name: "NONU Brightening Peel", desc: "Powerful blend of brightening agents to refine skin and restore clarity and luminosity.", prices: [{ l: "45 Minutes", p: "$127" }, { l: "Series of 3", p: "$345" }] },
      { name: "NONU Lactic Peel", desc: "Increases hydration for firmer, more supple skin. Recommended for dry, mature skin.", prices: [{ l: "45 Minutes", p: "$127" }, { l: "Series of 3", p: "$345" }] },
    ],
  },
  {
    id: "injectables", name: "Injectables",
    services: [
      { heading: "Botox" },
      { name: "NONU Eyebrow Lift (Botox)", prices: [{ l: "45 Minutes", p: "$280" }] },
      { name: "NONU Crows Feet Botox", prices: [{ l: "30 Minutes", p: "$260" }] },
      { name: "NONU Neck Botox", prices: [{ l: "30 Minutes", p: "$450" }] },
      { name: "NONU Gummy Smile", prices: [{ l: "1 Hour", p: "$350" }] },
      { name: "NONU Lip Flip", prices: [{ l: "30 Minutes", p: "$350" }] },
      { heading: "Lip Filler" },
      { name: "Natural Lips (1 syringe)", desc: "For the natural look. Results will be very minimal.", prices: [{ l: "1 Hour", p: "$375" }] },
      { name: "NONU Signature Lips (1 syringe)", prices: [{ l: "1 Hour", p: "$460" }] },
      { name: "NONU Russian Lips (1.5 syringe)", prices: [{ l: "1 Hour", p: "$550" }] },
      { name: "Signature Lip Filler w/ Teeth Whitening", prices: [{ l: "2 Hours", p: "$600" }] },
      { name: "Bring A Friend Lip Filler", desc: "1 syringe per person.", prices: [{ l: "2 Hours", p: "$850" }] },
      { name: "Lip Filler & Ombre Brow Duo", prices: [{ l: "1 Hour", p: "$700" }] },
      { heading: "Facial Balancing" },
      { name: "NONU Pixie Nose", prices: [{ l: "1 Hour", p: "$460" }] },
      { name: "Under Eye Rejuvenation / Brightening", prices: [{ l: "1 Hour", p: "$500" }] },
      { name: "Chin Filler", prices: [{ l: "45 Min\u20131 Hour", p: "$460\u2013$500" }] },
      { name: "Cheek Filler", prices: [{ l: "1 Hour", p: "$800" }] },
      { name: "Jawline Filler", desc: "1 syringe each side. Lasts 9\u201312 months.", prices: [{ l: "1 Hour", p: "$800" }] },
      { name: "Laugh Lines", desc: "1 syringe on each side.", prices: [{ l: "1 Hour", p: "$800" }] },
      { name: "Temple Filler", desc: "0.5 on each side.", prices: [{ l: "1 Hour", p: "$500" }] },
      { name: "Nostril Slimming", prices: [{ l: "30 Minutes", p: "$250" }] },
      { heading: "PDO Threads" },
      { name: "NONU PDO Threads", desc: "Custom placement for a sultry fox eye lift.", prices: [{ l: "1 Hour", p: "$1,500" }] },
      { name: "NONU Soft Lift w/ PDO Threads", desc: "4 threads each side.", prices: [{ l: "1 Hour", p: "$2,000" }] },
      { name: "NONU Lift w/ PDO Threads (12)", desc: "Eye lift, cheek lift, jawline contouring, neck lift. 6 threads each side.", prices: [{ l: "1 Hour", p: "$3,000" }] },
      { name: "NONU Double Chin Thread Lift", prices: [{ l: "1.5 Hours", p: "$1,500" }] },
      { heading: "Signature Packages" },
      { name: "NONU GOLD PACKAGE", desc: "Threads (eyes, cheeks, jawline), cheek filler, chin filler, signature lips, jawline filler, Botox forehead.", prices: [{ l: "2 Hr 40 Min", p: "$4,500" }] },
    ],
  },
  {
    id: "body-sculpting", name: "Body Sculpting",
    services: [
      { name: "Ultimate Body Contouring", prices: [{ l: "1 Hour", p: "$150" }] },
      { name: "Package of 3 Body Contouring", prices: [{ l: "1 Hour ea.", p: "$500" }] },
      { name: "Package of 6 Body Contouring", prices: [{ l: "1 Hour ea.", p: "$700" }] },
      { name: "NONU Hips", prices: [{ l: "3 Hours", p: "$2,660" }] },
      { name: "NONU SILHOUETTE\u2122 Liquid BBL", prices: [{ l: "3 Hours", p: "$3,560" }] },
      { name: "NONU Liquid BBL (800ml)", prices: [{ l: "3 Hr 10 Min", p: "$4,500" }] },
      { name: "NONU Fat Dissolver (x10)", desc: "Some fat-freezing treatments can cost $750+ per area.", prices: [{ l: "30 Minutes", p: "$1,000" }] },
      { heading: "Advanced Skin Treatments" },
      { name: "Microneedling", prices: [{ p: "$350\u2013$600" }] },
      { name: "Microneedling + PRP", prices: [{ p: "$700\u2013$1,000" }] },
      { name: "IPL Photofacial", prices: [{ p: "$400\u2013$700" }] },
      { name: "Laser Skin Resurfacing", prices: [{ p: "$800\u2013$2,500" }] },
      { name: "CO2 Laser Resurfacing", prices: [{ p: "$1,500\u2013$3,000" }] },
    ],
  },
  {
    id: "laser", name: "Laser Hair Removal",
    services: [
      { name: "Upper Lip", prices: [{ p: "$75" }] },
      { name: "Underarms", prices: [{ p: "$125" }] },
      { name: "Bikini", prices: [{ p: "$175" }] },
      { name: "Brazilian", prices: [{ p: "$250" }] },
      { name: "Full Legs", prices: [{ p: "$450" }] },
      { name: "Full Body Package", prices: [{ p: "$2,000\u2013$3,500" }] },
    ],
  },
  {
    id: "relaxation", name: "Relaxation",
    intro: "Our skilled therapists are certified in a variety of massage techniques. All services include an Aveda Aroma Sensory Journey and custom oil blending.",
    services: [
      { name: "NONU Aroma Relaxation", desc: "Customized massage combining Swedish, deep tissue, reflexology, and stress-relieving techniques.", prices: [{ l: "\u00bd Hour", p: "$68" }, { l: "\u00be Hour", p: "$83" }, { l: "1 Hour", p: "$98" }, { l: "1\u00bd Hour", p: "$149" }, { l: "2 Hours", p: "$190" }] },
      { name: "NONU Fusion Stone Relaxation", desc: "Polished warm river stones loosen muscles and flush toxins.", prices: [{ l: "\u00bd Hour", p: "$80" }, { l: "1 Hour", p: "$117" }, { l: "1\u00bd Hour", p: "$178" }] },
      { name: "NONU Himalayan Salt Stone", desc: "Heated salt stones deliver 84 naturally occurring minerals and salts.", prices: [{ l: "\u00bd Hour", p: "$80" }, { l: "1 Hour", p: "$117" }, { l: "1\u00bd Hour", p: "$178" }] },
      { name: "NONU Cupping", desc: "Stationary cups promote natural healing of chronic pain areas.", prices: [{ l: "\u00bd Hour", p: "$80" }] },
      { name: "NONU Myofascial Dual Cupping", desc: "Silicone and stationary cups stretch muscle tissue, increase circulation and remove toxins.", prices: [{ l: "1 Hour", p: "$120" }, { l: "1\u00bd Hour", p: "$170" }, { l: "2 Hours", p: "$210" }] },
      { name: "NONU Manual Lymphatic Drainage (MLD)", desc: "Light skin-stretching to reduce swelling and promote relaxation. Physician's note required within 6 weeks of surgery.", prices: [{ l: "\u00bd Hour", p: "$75" }, { l: "1 Hour", p: "$110" }, { l: "1\u00bd Hour", p: "$140" }] },
      { name: "NONU Pre-Natal Relaxation", desc: "Relieves lower back discomfort, water retention, and improves circulation. Not available first trimester.", prices: [{ l: "\u00bd Hour", p: "$68" }, { l: "1 Hour", p: "$98" }, { l: "1\u00bd Hour", p: "$149" }] },
      { name: "Relaxation Chair", desc: "Head, neck, and upper body. No oil, fully clothed.", prices: [{ l: "15 Minutes", p: "$35" }] },
      { name: "NONU Bamboo Roller Relaxation", desc: "Bamboo glides across the body for deep tissue therapy and stress relief.", prices: [{ l: "\u00bd Hour", p: "$75" }, { l: "1 Hour", p: "$115" }, { l: "1\u00bd Hour", p: "$175" }] },
      { name: "Couple's Relaxation / Facial", desc: "Any massage or facial performed in the same room with your partner.", prices: [{ p: "Same service pricing" }] },
      { heading: "Massage Add-Ons" },
      { name: "More Massage", prices: [{ p: "$26 / 15 min" }] },
      { name: "Gua Sha Techniques", prices: [{ p: "$10" }] },
      { name: "Aveda Aroma Foot Scrub", prices: [{ p: "$16" }] },
      { name: "Full-Body Dry Brush", desc: "Includes brush to take home.", prices: [{ p: "$26" }] },
      { name: "Fusion or Himalayan Salt Stones (2\u20134)", prices: [{ p: "$11" }] },
      { name: "Therapeutic Cups", prices: [{ p: "$20" }] },
      { name: "Paraffin On Hands & Feet", prices: [{ p: "$20" }] },
      { name: "Bamboo Sticks", prices: [{ p: "$11" }] },
    ],
  },
  {
    id: "body-treatments", name: "Body Treatments",
    services: [
      { name: "NONU Bare Facial (Fanny Facial)", desc: "Targets hard to reach areas, brightens skin, aids in reducing cellulite, clears acne, scarring, and hyperpigmentation.", prices: [{ l: "45 Minutes", p: "$140+" }, { l: "Hydrojelly Mask add-on", p: "$50" }] },
      { name: "NONU Back Treatment", desc: "Deep-cleansing for back, neck and shoulders. Includes exfoliation, extractions, light massage, and masque.", prices: [{ l: "45 Minutes", p: "$125+" }, { l: "Hydrojelly Mask add-on", p: "$50" }] },
      { name: "NONU Aroma Salt Glow", desc: "Mineral-rich sea salts combined with skin conditioning oils.", prices: [{ l: "No Steam \u2013 \u00bd Hr", p: "$85" }, { l: "With Steam \u2013 1 Hr", p: "$120" }] },
      { name: "NONU Aroma Body Polish", desc: "Finely-pulverized walnut shells with coconut oil-based cleanser.", prices: [{ l: "No Steam \u2013 \u00bd Hr", p: "$85" }, { l: "With Steam \u2013 1 Hr", p: "$120" }] },
      { name: "NONU Aroma Body Treatment", desc: "Detoxifying seaweed body masque. Includes dry exfoliation, scalp and face massage and foot reflexology.", prices: [{ l: "No Steam \u2013 1 Hr", p: "$125" }, { l: "With Steam \u2013 75 Min", p: "$155" }] },
      { name: "NONU Aroma Scalp Massage", desc: "15-minute scalp massage customized with an Aveda aroma.", prices: [{ l: "Add-on", p: "$35" }] },
    ],
  },
  {
    id: "lashes-brows", name: "Lashes & Brows",
    services: [
      { name: "NONU Lash Lift & Tint", desc: "Semi-permanent lift and curl for 2\u20133 months.", prices: [{ p: "$88" }] },
      { name: "NONU Brow Lamination", desc: 'This "perm" for your brows sculpts naturally fuller looking brows.', prices: [{ p: "$88" }] },
      { name: "Brow Lamination, Brow Wax & Tint", desc: "Ultimate clean, bold, and flawless brow look.", prices: [{ p: "$125" }] },
      { name: "Brow Shaping & Tweezing", desc: "Precise eyebrow shaping. Ideal for sensitive skin or certain medications.", prices: [{ p: "$37" }] },
      { name: "NONU 10-Day Lash Extensions", desc: "Fuller lashes in 15 minutes. Event-ready, customizable.", prices: [{ p: "$60" }] },
      { name: "Strip & Party Lashes", prices: [{ p: "$15\u2013$40" }] },
      { name: "Lash Tint", prices: [{ p: "$27" }] },
      { name: "Brow Tint", prices: [{ p: "$25" }] },
      { name: "Lash & Brow Tint", prices: [{ p: "$50" }] },
      { name: "Brow Wax & Brow Tint", prices: [{ p: "$50" }] },
    ],
  },
  {
    id: "waxing", name: "Waxing",
    intro: "Please have at least 3\u20134 weeks of hair growth. Exfoliate 1\u20132 days before. Avoid tanning and moisturizer day of. Arrivals 10+ minutes late may be rescheduled.",
    services: [
      { heading: "Face" },
      { name: "Brows", prices: [{ p: "$25" }] }, { name: "Brows & Chin", prices: [{ p: "$37" }] }, { name: "Brows & Lip", prices: [{ p: "$37" }] },
      { name: "Eyebrow, Lip & Chin", prices: [{ p: "$55" }] }, { name: "Sideburns", prices: [{ p: "$17+" }] }, { name: "Jawline", prices: [{ p: "$28+" }] },
      { name: "Lip, Chin, Ears or Nose", prices: [{ p: "$17" }] }, { name: "Full Face", prices: [{ p: "$68+" }] },
      { heading: "Body \u2013 Women" },
      { name: "Underarm", prices: [{ p: "$32+" }] }, { name: "Half Arm", prices: [{ p: "$60+" }] }, { name: "Full Arm", prices: [{ p: "$70+" }] },
      { name: "Half Leg", prices: [{ p: "$70+" }] }, { name: "Full Leg", prices: [{ p: "$90+" }] },
      { name: "Chest", prices: [{ p: "$67" }] }, { name: "Abdomen", prices: [{ p: "$68+" }] },
      { name: "Bikini", prices: [{ p: "$42+" }] }, { name: "French Bikini", prices: [{ p: "$52+" }] }, { name: "Brazilian", prices: [{ p: "$90+" }] },
      { name: "Brazilian Maintenance (up to 5 wks)", prices: [{ p: "$72+" }] },
      { name: "Partial Glutes", prices: [{ p: "$32+" }] }, { name: "Full Glutes", prices: [{ p: "$47+" }] },
      { name: "Small of the Back", prices: [{ p: "$23+" }] },
      { heading: "Body \u2013 Men" },
      { name: "Men's Bikini", prices: [{ p: "$93+" }] }, { name: "Men's Brazilian", prices: [{ p: "$130+" }] }, { name: "Men's Brazilian Maintenance", prices: [{ p: "$110+" }] },
      { name: "Men's Glutes \u2013 Partial", prices: [{ p: "$48+" }] }, { name: "Men's Glutes \u2013 Full", prices: [{ p: "$65+" }] },
      { name: "Men's Back", prices: [{ p: "$90+" }] }, { name: "Men's Shoulders", prices: [{ p: "$42+" }] }, { name: "Men's Underarm", prices: [{ p: "$37" }] },
      { name: "Men's Full Chest", prices: [{ p: "$78+" }] }, { name: "Men's Abdomen", prices: [{ p: "$90+" }] },
      { name: "Men's Full Arm", prices: [{ p: "$78+" }] }, { name: "Men's Half Arm", prices: [{ p: "$68+" }] },
      { name: "Men's Full Leg", prices: [{ p: "$130+" }] }, { name: "Men's Half Leg", prices: [{ p: "$88+" }] },
      { name: "Men's Nape of Neck", prices: [{ p: "$36" }] },
    ],
  },
  {
    id: "addons", name: "Enhancements",
    services: [
      { name: "NONU Energizing Eye Treatment", desc: "Soothe tension, diminish dark circles, reduce puffiness.", prices: [{ l: "15 Min", p: "$27" }] },
      { name: "NONU Renewal Eye Treatment", desc: "PHA exfoliation, B\u00b2 eye mask, antioxidant eye creams. Smooths fine lines, reduces dark circles.", prices: [{ l: "15 Min", p: "$35" }] },
      { name: "NONU Elite LED Therapy", desc: "Kills acne bacteria, stimulates collagen, boosts cellular metabolism.", prices: [{ l: "30 Min", p: "$47" }, { l: "15 Min", p: "$26" }] },
      { name: "Double Down Extractions", desc: "Clear complexion, less congestion, better product absorption.", prices: [{ l: "15 Min", p: "$30" }] },
      { name: "NONU Gua Sha", desc: "Promotes lymphatic drainage, improves circulation, relieves puffiness, smooths fine lines.", prices: [{ l: "15 Min", p: "$27" }] },
      { name: "NuPeel", desc: "Natural enzyme exfoliation. Smooths skin, evens tone, accelerates cell turnover.", prices: [{ p: "$30" }] },
      { name: "Happy Peel (NO Peel)", desc: "Lactic Acid, Salicylic Acid, Retinol, Mandelic Acid blend. No downtime, all skin types.", prices: [{ p: "$27" }] },
      { name: "NONU Even Tone Peel", desc: "Brightening treatment to revive dull or uneven skin.", prices: [{ l: "15 Min", p: "$40" }] },
      { name: "Dermaplaning Add-on", prices: [{ l: "30 Min", p: "$68" }] },
      { name: "NONU Hydrojelly Mask", desc: "Hydrate, smooth, moisturize, and reduce inflammation.", prices: [{ l: "Face", p: "$28" }, { l: "Fanny or Back", p: "$50" }] },
      { name: "NONU Beard Boost Add-on", desc: "Deep cleanse, condition, and refine beard and skin beneath.", prices: [{ l: "30 Min", p: "$45" }] },
    ],
  },
];

// Treatments Page
const Treatments = () => {
  const [activeTab, setActiveTab] = useState("facials");
  const activeCategory = SERVICE_MENU.find(c => c.id === activeTab);

  return (
    <main data-testid="treatments-page" className="bg-[#f8f4ef]">
      {/* Hero */}
      <section className="pt-32 pb-8 px-6 text-center section-cream-light">
        <AnimatedSection>
          <p className="text-[#c9a050] text-xs tracking-[0.25em] uppercase mb-4 font-semibold">NONU Aesthetic & Wellness</p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-[#2d261f] mb-4 italic">Service Menu & Pricing</h1>
          <p className="text-[#7a6b5a] max-w-2xl mx-auto mb-6">Refined Beauty. Elevated Confidence.</p>
          <div className="max-w-2xl mx-auto bg-[#2d261f] text-[#f8f4ef] p-4 rounded-lg text-xs leading-relaxed" data-testid="consultation-notice">
            <p className="font-semibold text-[#c9a050] mb-1">CONSULTATION POLICY</p>
            <p>NO REFUNDS FOR VIRTUAL OR IN-PERSON CONSULTATIONS ONCE APPOINTMENT IS SCHEDULED.</p>
            <p className="mt-1 text-[#a89b8a]">Please have questions ready &mdash; consultations are 30 minutes.</p>
          </div>
        </AnimatedSection>
      </section>

      {/* Category Tabs */}
      <section className="sticky top-[72px] z-30 bg-[#f8f4ef] border-b border-[#e5ddd0] shadow-sm">
        <div className="container-max overflow-x-auto scrollbar-hide">
          <div className="flex gap-1 px-4 py-3 min-w-max">
            {SERVICE_MENU.map(cat => (
              <button
                key={cat.id}
                data-testid={`tab-${cat.id}`}
                onClick={() => setActiveTab(cat.id)}
                className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold rounded-full whitespace-nowrap transition-all ${
                  activeTab === cat.id
                    ? 'bg-[#c9a050] text-white shadow-md'
                    : 'text-[#7a6b5a] hover:bg-[#e5ddd0] hover:text-[#2d261f]'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="section-padding section-cream" data-testid="services-list">
        <div className="container-max max-w-4xl">
          {activeCategory && (
            <AnimatedSection key={activeTab} animation="fade-in">
              <h2 className="font-display text-3xl md:text-4xl text-[#2d261f] mb-4 italic">{activeCategory.name}</h2>
              {activeCategory.intro && <p className="text-[#7a6b5a] mb-4 text-sm leading-relaxed">{activeCategory.intro}</p>}
              {activeCategory.note && <p className="text-[#c9a050] mb-6 text-xs italic">{activeCategory.note}</p>}
              <div className="w-16 h-0.5 bg-[#c9a050] mb-8" />

              <div className="space-y-1">
                {activeCategory.services.map((svc, idx) => (
                  svc.heading ? (
                    <div key={idx} className="pt-8 pb-3">
                      <h3 className="font-display text-xl text-[#c9a050]">{svc.heading}</h3>
                      <div className="w-12 h-0.5 bg-[#c9a050]/30 mt-2" />
                    </div>
                  ) : (
                    <div key={idx} className="py-4 border-b border-[#e5ddd0]/60 group hover:bg-white/50 px-4 -mx-4 rounded-lg transition-colors" data-testid={`service-item-${idx}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-[#2d261f] font-medium text-sm">{svc.name}</h4>
                          {svc.desc && <p className="text-[#7a6b5a] text-xs mt-1 leading-relaxed">{svc.desc}</p>}
                        </div>
                        {svc.prices.length === 1 && !svc.prices[0].l && (
                          <span className="text-[#c9a050] font-semibold text-sm whitespace-nowrap">{svc.prices[0].p}</span>
                        )}
                      </div>
                      {(svc.prices.length > 1 || svc.prices[0].l) && (
                        <div className="mt-2 space-y-1">
                          {svc.prices.map((pr, pi) => (
                            <div key={pi} className="flex justify-between text-xs">
                              <span className="text-[#7a6b5a]">{pr.l || ''}</span>
                              <span className="text-[#c9a050] font-semibold">{pr.p}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <Link to="/book" className="inline-block mt-3 text-xs uppercase tracking-wider font-semibold text-[#c9a050] hover:text-[#2d261f] transition-colors" data-testid={`book-service-${idx}`}>
                        Book Now <ChevronRight size={12} className="inline" />
                      </Link>
                    </div>
                  )
                ))}
              </div>
            </AnimatedSection>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding section-cream-dark text-center">
        <AnimatedSection>
          <h2 className="font-display text-3xl md:text-4xl text-[#2d261f] mb-6 italic">Ready to Begin?</h2>
          <p className="text-[#7a6b5a] mb-8 max-w-lg mx-auto">Schedule your personalized consultation today.</p>
          <Link to="/book" className="btn-gold" data-testid="treatments-cta-book">Book Your Consultation</Link>
        </AnimatedSection>
      </section>
    </main>
  );
};

// Default membership data (fallback)
const DEFAULT_MEMBERSHIPS = {
  tiers: [
    {
      name: "NONU Essential",
      price: "$149/month",
      tagline: "Maintenance & New Clientele",
      features: [
        "1 Signature Facial monthly",
        "10% off all services",
        "$50 monthly treatment credit (rollover 2 months)",
        "Complimentary skin analysis",
        "Member-only booking priority",
      ],
      cta: "Join Essential",
    },
    {
      name: "NONU Glow",
      price: "$299/month",
      tagline: "Advanced Care & VIP Perks",
      features: [
        "1 Advanced Facial or Chemical Peel",
        "1 Add-on (LED, dermaplaning, hydro infusion)",
        "15% off services + retail",
        "$100 monthly treatment bank",
        "Birthday luxury service upgrade",
        "Access to VIP events",
      ],
      cta: "Join Glow",
      featured: true,
    },
    {
      name: "NONU Elite",
      price: "$599/month",
      tagline: "Medical-Grade Results",
      features: [
        "1 Medical Treatment monthly (Microneedling, Laser, or Acne Therapy)",
        "$200 monthly injectable bank (Botox/Filler)",
        "20% off all services",
        "Complimentary IV therapy or body contour session",
        "Same-week priority booking guaranteed",
        "Exclusive member concierge",
      ],
      cta: "Join Elite",
    },
    {
      name: "NONU Black Card",
      price: "$1,500–$2,500/month",
      tagline: "The Ultimate Luxury",
      features: [
        "Unlimited facials (fair use)",
        "2 premium treatments/month (Laser, RF, Microneedling, Body Sculpting)",
        "$500–$1,000 injectable bank monthly",
        "Complimentary IV therapy, peptides, detox & lymphatic treatments",
        "Private VIP treatment days",
        "Quarterly home skincare concierge kit",
        "Personal aesthetic plan + transformation tracking",
      ],
      cta: "Apply Now",
    },
  ]
};

// Membership Page
const Membership = () => {
  const memberships = DEFAULT_MEMBERSHIPS;

  const tierStyles = [
    'border-[#e5ddd0]',
    'border-[#c9a050]',
    'border-[#c9a050]',
    'bg-[#2d261f] border-[#c9a050]',
  ];

  return (
    <main data-testid="membership-page" className="bg-[#f8f4ef]">
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 text-center section-cream-light">
        <AnimatedSection>
          <p className="text-[#c9a050] text-xs tracking-[0.25em] uppercase mb-4 font-semibold">Exclusive Access</p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-[#2d261f] mb-4 italic">Membership</h1>
          <p className="text-[#7a6b5a] max-w-2xl mx-auto">
            Join the NONU inner circle for exclusive benefits and VIP treatment.
          </p>
        </AnimatedSection>
      </section>

      {/* Membership Tiers */}
      <section className="section-padding section-cream">
        <div className="container-max">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {memberships.tiers.map((tier, index) => {
              const isDark = index === 3;
              return (
                <AnimatedSection
                  key={tier.name}
                  animation="scale-in"
                  delay={index * 0.1}
                >
                  <div
                    data-testid={`membership-tier-${tier.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`relative p-8 rounded-2xl shadow-lg transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 border-2 ${tierStyles[index]} ${isDark ? 'bg-[#2d261f]' : 'bg-white'}`}
                  >
                    {tier.featured && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#c9a050] to-[#a68638] text-white text-xs font-bold px-6 py-2 uppercase tracking-wider rounded-full shadow-lg">
                        Most Popular
                      </div>
                    )}
                    {index === 3 && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-white text-[#2d261f] text-xs font-bold px-6 py-2 uppercase tracking-wider rounded-full shadow-lg">
                        VIP
                      </div>
                    )}
                    <h3 className={`font-display text-2xl mb-1 ${isDark ? 'text-[#f8f4ef]' : 'text-[#2d261f]'}`}>{tier.name}</h3>
                    <p className="text-[#c9a050] font-semibold text-lg mb-1">{tier.price}</p>
                    <p className={`text-sm mb-6 ${isDark ? 'text-[#a89b8a]' : 'text-[#7a6b5a]'}`}>{tier.tagline}</p>
                    <ul className="space-y-3 mb-8">
                      {tier.features.map((feature) => (
                        <li key={feature} className={`flex items-start gap-3 text-sm ${isDark ? 'text-[#a89b8a]' : 'text-[#7a6b5a]'}`}>
                          <ChevronRight size={14} className="text-[#c9a050] mt-1 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/book"
                      className={isDark ? "bg-gradient-to-r from-[#c9a050] to-[#a68638] text-white px-6 py-3 text-xs uppercase tracking-wider font-semibold w-full text-center block rounded-lg hover:opacity-90 transition-opacity" : "btn-gold w-full text-center block"}
                      data-testid={`join-${tier.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {tier.cta}
                    </Link>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding section-cream-light">
        <div className="container-max text-center">
          <AnimatedSection>
            <h2 className="font-display text-3xl md:text-4xl text-[#2d261f] mb-12 italic">Why Become a Member</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { icon: <Clock size={32} />, title: "Priority Booking", desc: "Same-week guaranteed scheduling" },
              { icon: <Star size={32} />, title: "Exclusive Savings", desc: "Up to 20% off all services" },
              { icon: <Award size={32} />, title: "Monthly Treatments", desc: "Premium services included" },
              { icon: <Users size={32} />, title: "VIP Events", desc: "Private member experiences" },
            ].map((benefit, idx) => (
              <AnimatedSection key={benefit.title} animation="fade-in-up" delay={idx * 0.1}>
                <div className="text-center p-6 bg-white rounded-xl shadow-md border border-[#c9a050]/10">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#c9a050]/10 flex items-center justify-center text-[#c9a050]">{benefit.icon}</div>
                  <h4 className="text-[#2d261f] font-medium mb-2">{benefit.title}</h4>
                  <p className="text-[#7a6b5a] text-sm">{benefit.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

// Default academy data (fallback)
const DEFAULT_ACADEMY = {
  school_name: "BBI – THE SCHOOL OF ADVANCEMENT",
  subtitle: "Certified Medical Aesthetic Advancement Programs",
  overview: "The BBI Advancement Program is a 4–6 week accelerated certification designed to train professionals in high-income medical aesthetic services including laser, skin correction, and advanced treatments.",
  graduates_receive: [
    "Certification (BBI Certified Medical Aesthetic Specialist)",
    "Hands-on clinical experience",
    "Business + client acquisition training",
    "Real med spa readiness",
  ],
  courses: [
    {
      name: "Laser & Device Certification",
      price: "$5,200",
      description: "Learn to safely operate advanced laser and energy-based devices used in med spas.",
      includes: [
        "Laser Hair Removal (IPL, Diode, Nd:YAG)",
        "Laser Tattoo Removal",
        "Skin Rejuvenation (photofacials, pigmentation)",
        "RF Skin Tightening & Body Contouring",
        "Fitzpatrick skin typing + treatment planning",
        "Safety + compliance (OSHA/ANSI)",
      ],
    },
    {
      name: "Injectables Theory + Observation",
      price: "$4,850",
      description: "Foundational knowledge of cosmetic injectables with medical collaboration positioning.",
      includes: [
        "Botox & neuromodulators (theory)",
        "Dermal fillers (lips, contouring)",
        "Facial anatomy + safety zones",
        "Complication management awareness",
        "Consultation + client mapping",
      ],
    },
    {
      name: "Microneedling + Skin Correction",
      price: "$2,850",
      description: "Advanced skin rejuvenation training focused on collagen induction and correction treatments.",
      includes: [
        "Microneedling + RF microneedling",
        "Acne, hyperpigmentation, scarring",
        "Chemical peels (light–advanced)",
        "Skin analysis + treatment planning",
        "Product layering + protocols",
      ],
    },
    {
      name: "Specialty: Foot Fungus Laser / Medical Skin",
      price: "$2,500",
      description: "A niche, high-profit treatment category most schools do NOT offer.",
      includes: [
        "Laser fungus treatment protocols",
        "Sanitation + infection control",
        "Medical-grade consultations",
        "Pricing + package structuring",
      ],
    },
    {
      name: "Med Spa Business & Wealth Training",
      price: "$1,500 add-on",
      description: "Learn how to make money immediately after certification. This is what makes BBI DIFFERENT.",
      includes: [
        "Pricing high-ticket services ($1K–$5K packages)",
        "Membership models (recurring income)",
        "Social media + luxury branding",
        "Client acquisition systems",
        "Equipment purchasing strategy",
      ],
    },
  ],
  packages: [
    {
      name: "Standard Enrollment",
      title: "BBI Certified Medical Aesthetic Diploma (4–6 Weeks)",
      includes: ["Laser Certification", "Injectables Theory", "Skin + Microneedling", "Specialty Treatments", "Business Training", "Hands-on Clinical Practice"],
      price: "$7,500",
    },
    {
      name: "VIP / Private Cohort",
      title: "BBI Certified Medical Aesthetic Diploma",
      includes: ["Everything in Standard", "Small group or private sessions", "Priority scheduling", "Extended clinical hours"],
      price: "$12,500",
    },
    {
      name: "Elite Mentorship",
      title: "Done-With-You Business Setup",
      includes: ["Everything in VIP", "1-on-1 mentorship", "Business setup assistance", "Client acquisition strategy", "Ongoing support"],
      price: "$15,000",
    },
  ],
  mentorship: [
    { duration: "30 Days", price: "$600" },
    { duration: "60 Days", price: "$900" },
    { duration: "90 Days", price: "$1,200" },
  ],
  pmu_training: {
    title: "Advanced PMU Training",
    intro: "At NONU Aesthetics & Wellness, our Advanced PMU Training is designed for serious beauty professionals ready to step into high-income, high-demand services. This immersive, hands-on training delivers precision techniques, advanced artistry, and real client experience in a luxury learning environment.",
    options: [
      {
        name: "4-Day Advanced PMU Intensive",
        price: "$4,800",
        includes: [
          "Advanced brow techniques (nano hair strokes + shading)",
          "Lip blush & color correction",
          "Eyeliner application fundamentals",
          "Skin anatomy + pigment theory",
          "Live model practice",
          "Business & pricing strategy",
          "Certification of Completion"
        ]
      },
      {
        name: "2-Day PMU Fast Track",
        price: "$3,800",
        includes: [
          "Brow techniques (nano hair strokes + shading)",
          "Intro to lip blush",
          "Hands-on guided practice",
          "Certification of Completion"
        ]
      }
    ],
    notice: {
      title: "Important Notice",
      content: "Participation is intended for licensed professionals, or individuals eligible to perform services based on their state's regulations and scope of practice. NONU Aesthetics & Wellness does not override state laws or licensing requirements. It is the responsibility of each student to ensure compliance with their local state board and regulatory guidelines prior to enrollment and service practice.",
      footnote: "( out of state students & apprenticeships welcomed )"
    }
  }
};

// Academy (BBI) Page
const Academy = () => {
  const academy = DEFAULT_ACADEMY;

  return (
    <main data-testid="academy-page" className="bg-[#f8f4ef]">
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 text-center section-cream-light">
        <AnimatedSection>
          <p className="text-[#c9a050] text-xs tracking-[0.25em] uppercase mb-4 font-semibold">Professional Training</p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-[#2d261f] mb-4 italic">BBI &mdash; The School of Advancement</h1>
          <p className="text-[#7a6b5a] max-w-2xl mx-auto mb-4">Certified Medical Aesthetic Advancement Programs</p>
          <p className="text-[#7a6b5a] max-w-3xl mx-auto text-sm">{academy.overview}</p>
        </AnimatedSection>
      </section>

      {/* What Students Receive */}
      <section className="section-padding section-cream">
        <div className="container-max">
          <AnimatedSection>
            <h2 className="font-display text-3xl text-[#2d261f] mb-8 italic text-center">Students Graduate With</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {academy.graduates_receive.map((item, idx) => (
              <AnimatedSection key={idx} animation="fade-in-up" delay={idx * 0.1}>
                <div className="bg-white p-6 rounded-xl border border-[#c9a050]/10 shadow-md text-center">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-[#c9a050]/10 flex items-center justify-center text-[#c9a050] font-bold text-sm">{idx + 1}</div>
                  <p className="text-[#2d261f] text-sm font-medium">{item}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {/* Courses */}
          <AnimatedSection>
            <h2 className="font-display text-3xl text-[#2d261f] mb-10 italic text-center">Core Advancement Courses</h2>
          </AnimatedSection>
          <div className="space-y-6">
            {academy.courses.map((course, idx) => (
              <AnimatedSection key={idx} animation="fade-in-up" delay={idx * 0.08}>
                <div className="bg-white rounded-2xl shadow-lg border border-[#e5ddd0] overflow-hidden" data-testid={`course-${idx}`}>
                  <div className="p-6 md:p-8">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                      <div>
                        <h3 className="font-display text-xl text-[#2d261f]">{course.name}</h3>
                        <p className="text-[#7a6b5a] text-sm mt-1">{course.description}</p>
                      </div>
                      <span className="text-[#c9a050] font-bold text-xl whitespace-nowrap">{course.price}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {course.includes.map((item, ii) => (
                        <div key={ii} className="flex items-start gap-2 text-sm text-[#7a6b5a]">
                          <ChevronRight size={12} className="text-[#c9a050] mt-1 flex-shrink-0" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Full Program Packages */}
      <section className="section-padding section-cream-dark">
        <div className="container-max">
          <AnimatedSection>
            <h2 className="font-display text-3xl text-[#2d261f] mb-4 italic text-center">BBI Signature Program</h2>
            <p className="text-[#7a6b5a] text-center mb-10 text-sm">Certified Medical Aesthetic Diploma (4&ndash;6 Weeks)</p>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {academy.packages.map((pkg, idx) => (
              <AnimatedSection key={idx} animation="scale-in" delay={idx * 0.1}>
                <div className={`p-8 rounded-2xl shadow-lg transition-all duration-300 hover:-translate-y-2 ${
                  idx === 2 ? 'bg-[#2d261f] border-2 border-[#c9a050]' : 'bg-white border-2 border-[#e5ddd0]'
                }`} data-testid={`package-${idx}`}>
                  <p className="text-[#c9a050] text-xs uppercase tracking-wider font-semibold mb-2">{pkg.name}</p>
                  <p className={`font-display text-4xl mb-4 ${idx === 2 ? 'text-[#c9a050]' : 'text-[#2d261f]'}`}>{pkg.price}</p>
                  <ul className="space-y-2 mb-6">
                    {pkg.includes.map((item, ii) => (
                      <li key={ii} className={`flex items-start gap-2 text-sm ${idx === 2 ? 'text-[#a89b8a]' : 'text-[#7a6b5a]'}`}>
                        <ChevronRight size={12} className="text-[#c9a050] mt-1 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/book" className={idx === 2 ? "bg-gradient-to-r from-[#c9a050] to-[#a68638] text-white px-6 py-3 text-xs uppercase tracking-wider font-semibold w-full text-center block rounded-lg" : "btn-gold w-full text-center block"} data-testid={`enroll-${idx}`}>
                    Enroll Now
                  </Link>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {/* Mentorship */}
          <AnimatedSection>
            <div className="mt-16 text-center">
              <h3 className="font-display text-2xl text-[#2d261f] mb-6 italic">Mentorship Programs</h3>
              <div className="flex flex-wrap justify-center gap-6">
                {academy.mentorship.map((m, idx) => (
                  <div key={idx} className="bg-white p-6 rounded-xl border border-[#c9a050]/10 shadow-md text-center min-w-[180px]">
                    <p className="text-[#7a6b5a] text-sm mb-2">{m.duration}</p>
                    <p className="text-[#c9a050] font-bold text-xl">{m.price}</p>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Advanced PMU Training Section */}
      <section className="section-padding section-cream" data-testid="pmu-training-section">
        <div className="container-max">
          <AnimatedSection>
            <div className="gold-line mx-auto mb-6" />
            <h2 className="font-display text-3xl md:text-4xl text-[#2d261f] mb-4 italic text-center">{academy.pmu_training.title}</h2>
            <p className="text-[#7a6b5a] text-center mb-12 max-w-4xl mx-auto leading-relaxed">{academy.pmu_training.intro}</p>
          </AnimatedSection>

          {/* Training Options Header */}
          <AnimatedSection>
            <h3 className="font-display text-2xl text-[#2d261f] mb-8 italic text-center">Training Options</h3>
          </AnimatedSection>

          {/* Training Options Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {academy.pmu_training.options.map((option, idx) => (
              <AnimatedSection key={idx} animation="scale-in" delay={idx * 0.15}>
                <div className={`p-8 rounded-2xl shadow-lg transition-all duration-300 hover:-translate-y-2 h-full ${
                  idx === 0 ? 'bg-[#2d261f] border-2 border-[#c9a050]' : 'bg-white border-2 border-[#e5ddd0]'
                }`} data-testid={`pmu-option-${idx}`}>
                  <p className="text-[#c9a050] text-xs uppercase tracking-wider font-semibold mb-2">Training Option</p>
                  <h4 className={`font-display text-xl mb-2 ${idx === 0 ? 'text-white' : 'text-[#2d261f]'}`}>{option.name}</h4>
                  <p className={`font-display text-3xl mb-6 ${idx === 0 ? 'text-[#c9a050]' : 'text-[#c9a050]'}`}>
                    Investment: {option.price}
                  </p>
                  <ul className="space-y-3 mb-6">
                    {option.includes.map((item, ii) => (
                      <li key={ii} className={`flex items-start gap-3 text-sm ${idx === 0 ? 'text-[#a89b8a]' : 'text-[#7a6b5a]'}`}>
                        <ChevronRight size={14} className="text-[#c9a050] mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Link 
                    to="/book" 
                    className={idx === 0 
                      ? "bg-gradient-to-r from-[#c9a050] to-[#a68638] text-white px-6 py-3 text-xs uppercase tracking-wider font-semibold w-full text-center block rounded-lg hover:opacity-90 transition-opacity" 
                      : "btn-gold w-full text-center block"
                    } 
                    data-testid={`pmu-enroll-${idx}`}
                  >
                    Enroll Now
                  </Link>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {/* Important Notice */}
          <AnimatedSection animation="fade-in-up">
            <div className="bg-white rounded-2xl shadow-lg border-2 border-[#c9a050]/30 p-8 max-w-4xl mx-auto" data-testid="pmu-notice">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#c9a050]/10 flex items-center justify-center flex-shrink-0">
                  <AlertCircle size={24} className="text-[#c9a050]" />
                </div>
                <div>
                  <h4 className="font-display text-xl text-[#2d261f] mb-3">{academy.pmu_training.notice.title}</h4>
                  <p className="text-[#7a6b5a] text-sm leading-relaxed mb-4">{academy.pmu_training.notice.content}</p>
                  <p className="text-[#c9a050] text-sm font-medium italic">{academy.pmu_training.notice.footnote}</p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding section-cream-dark text-center">
        <AnimatedSection>
          <h2 className="font-display text-3xl md:text-4xl text-[#2d261f] mb-6 italic">Start Your Career Transformation</h2>
          <p className="text-[#7a6b5a] mb-8 max-w-lg mx-auto text-sm">Limited enrollment. Apply now for the next cohort.</p>
          <Link to="/book" className="btn-gold" data-testid="academy-cta-enroll">Apply Now</Link>
        </AnimatedSection>
      </section>
    </main>
  );
};

// About Page
const About = () => {
  return (
    <main data-testid="about-page" className="bg-[#f8f4ef]">
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 text-center section-cream-light">
        <AnimatedSection>
          <p className="text-[#c9a050] text-xs tracking-[0.25em] uppercase mb-4 font-semibold">Our Story</p>
          <h1 className="font-display text-5xl md:text-6xl text-[#2d261f] mb-4 italic">About NONU</h1>
          <p className="text-[#7a6b5a] max-w-2xl mx-auto">
            A luxury aesthetic & wellness house dedicated to transformative results.
          </p>
        </AnimatedSection>
      </section>

      {/* Mission */}
      <section className="section-padding section-cream">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection animation="slide-left">
              <div className="gold-line mb-6" />
              <h2 className="font-display text-3xl md:text-4xl text-[#2d261f] mb-6 italic">Redefining Beauty Standards</h2>
              <p className="text-[#7a6b5a] leading-relaxed mb-6">
                At NONU Aesthetic & Wellness, we believe that true beauty is the result of precision, artistry, and personalized care. Our mission is to provide transformative treatments that enhance your natural features.
              </p>
              <p className="text-[#7a6b5a] leading-relaxed">
                We are more than a medical spa—we are a luxury aesthetic house where science meets sophistication.
              </p>
            </AnimatedSection>
            <AnimatedSection animation="slide-right" className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-[#c9a050]/20">
                <img 
                  src="https://customer-assets.emergentagent.com/job_white-screen-issue/artifacts/hy4w8xry_fwj371cz_cae612a3-d17e-42b7-9768-0a457abac818.jpg"
                  alt="NONU Treatment"
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-24 h-24 border-2 border-[#c9a050] rounded-2xl -z-10" />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding section-cream-light">
        <div className="container-max text-center">
          <AnimatedSection className="mb-12">
            <div className="gold-line mx-auto mb-6" />
            <h2 className="font-display text-3xl md:text-4xl text-[#2d261f] italic">What Sets Us Apart</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { title: "Excellence", description: "We pursue perfection in every treatment, every interaction, and every result." },
              { title: "Integrity", description: "Honest consultations and realistic expectations are the foundation of trust." },
              { title: "Innovation", description: "We continuously adopt the latest technologies and techniques." },
            ].map((value, idx) => (
              <AnimatedSection key={value.title} animation="fade-in-up" delay={idx * 0.15}>
                <div className="p-8 bg-white rounded-xl shadow-md border border-[#c9a050]/10">
                  <h3 className="font-display text-2xl text-[#c9a050] mb-4">{value.title}</h3>
                  <p className="text-[#7a6b5a]">{value.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding section-cream-dark text-center">
        <AnimatedSection>
          <h2 className="font-display text-3xl md:text-4xl text-[#2d261f] mb-6 italic">Experience the Difference</h2>
          <p className="text-[#7a6b5a] mb-8 max-w-lg mx-auto">
            Discover why discerning clients choose NONU.
          </p>
          <Link to="/book" className="btn-gold" data-testid="about-cta-book">
            Book Your Visit
          </Link>
        </AnimatedSection>
      </section>
    </main>
  );
};

// Contact Page
const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_interest: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formBody = new URLSearchParams({
        "form-name": "contact",
        ...formData,
      }).toString();
      
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formBody,
      });
      toast.success("Thank you! We'll be in touch shortly.");
      setFormData({ name: "", email: "", phone: "", service_interest: "", message: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    "Consultation",
    "Facials & Hydrafacials",
    "Injectables & Fillers",
    "PDO Threads",
    "Body Sculpting",
    "Laser Hair Removal",
    "Relaxation & Massage",
    "Lashes & Brows",
    "Waxing",
    "BBI Academy",
    "Wellness Services",
    "Membership Inquiry",
    "Academy Programs",
    "Other"
  ];

  return (
    <main data-testid="contact-page" className="bg-[#f8f4ef]">
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 text-center section-cream-light">
        <AnimatedSection>
          <p className="text-[#c9a050] text-xs tracking-[0.25em] uppercase mb-4 font-semibold">Get in Touch</p>
          <h1 className="font-display text-5xl md:text-6xl text-[#2d261f] mb-4 italic">Contact Us</h1>
          <p className="text-[#7a6b5a] max-w-2xl mx-auto">
            Ready to begin your transformation? Reach out to schedule your consultation.
          </p>
        </AnimatedSection>
      </section>

      {/* Contact Form & Info */}
      <section className="section-padding section-cream">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <AnimatedSection animation="slide-left">
              <div className="bg-white p-8 lg:p-12 rounded-2xl shadow-xl border-2 border-[#c9a050]/10">
                <h2 className="font-display text-2xl text-[#2d261f] mb-8">Send a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form" data-netlify="true" name="contact">
                  <input type="hidden" name="form-name" value="contact" />
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your Name"
                      className="form-input"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Email Address"
                      className="form-input"
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Phone Number"
                      className="form-input"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <select
                      name="service_interest"
                      value={formData.service_interest}
                      onChange={handleChange}
                      className="form-input bg-transparent cursor-pointer"
                      data-testid="contact-service-select"
                    >
                      <option value="" className="bg-white">Select Service of Interest</option>
                      {services.map((service) => (
                        <option key={service} value={service} className="bg-white">{service}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Your Message"
                      rows={5}
                      className="form-input resize-none"
                      required
                      data-testid="contact-message-input"
                    />
                  </div>
                  <button 
                    type="submit" 
                    className="btn-gold w-full"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </button>
                </form>
              </div>
            </AnimatedSection>

            {/* Contact Info */}
            <AnimatedSection animation="slide-right">
              <h2 className="font-display text-2xl text-[#2d261f] mb-8">Visit Us</h2>
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#c9a050]/10 border border-[#c9a050]/30 flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#c9a050]" size={20} />
                  </div>
                  <div>
                    <h3 className="text-[#2d261f] font-medium mb-1">Location</h3>
                    <p className="text-[#7a6b5a]">801 S. Bowman, Suite 4<br />Little Rock, AR 72201</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#c9a050]/10 border border-[#c9a050]/30 flex items-center justify-center flex-shrink-0">
                    <Phone className="text-[#c9a050]" size={20} />
                  </div>
                  <div>
                    <h3 className="text-[#2d261f] font-medium mb-1">Phone</h3>
                    <a href="tel:+15015543307" className="text-[#7a6b5a] hover:text-[#c9a050] transition-colors">(501) 554-3307</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#c9a050]/10 border border-[#c9a050]/30 flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#c9a050]" size={20} />
                  </div>
                  <div>
                    <h3 className="text-[#2d261f] font-medium mb-1">Email</h3>
                    <a href="mailto:hello@nonuaesthetics.com" className="text-[#7a6b5a] hover:text-[#c9a050] transition-colors">hello@nonuaesthetics.com</a>
                    <div className="mt-2 space-y-1">
                      <a href="mailto:billing@nonuaesthetics.com" className="text-[#7a6b5a] hover:text-[#c9a050] transition-colors text-sm block">billing@nonuaesthetics.com</a>
                      <a href="mailto:admin@nonuaesthetics.com" className="text-[#7a6b5a] hover:text-[#c9a050] transition-colors text-sm block">admin@nonuaesthetics.com</a>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#c9a050]/10 border border-[#c9a050]/30 flex items-center justify-center flex-shrink-0">
                    <Clock className="text-[#c9a050]" size={20} />
                  </div>
                  <div>
                    <h3 className="text-[#2d261f] font-medium mb-1">Hours</h3>
                    <p className="text-[#7a6b5a]">
                      Monday - Friday: 9am - 7pm<br />
                      Saturday: 10am - 5pm<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>

              {/* Google Map Embed */}
              <div className="mt-10 rounded-2xl overflow-hidden border-2 border-[#c9a050]/10 shadow-lg" data-testid="contact-map">
                <iframe
                  title="NONU Aesthetic & Wellness Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3271.5!2d-92.2896!3d34.7465!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87d2a134a11f569b%3A0x3405f5100df35b17!2s801%20S%20Bowman%20Rd%2C%20Little%20Rock%2C%20AR%2072211!5e0!3m2!1sen!2sus!4v1700000000000"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </main>
  );
};

// FAQ Accordion Item
const FAQItem = ({ question, answer, index }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div 
      data-testid={`faq-item-${index}`}
      className="border-b border-[#e5ddd0] last:border-b-0"
    >
      <button
        data-testid={`faq-toggle-${index}`}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <span className="font-display text-lg text-[#2d261f] group-hover:text-[#c9a050] transition-colors pr-4">{question}</span>
        <span className={`flex-shrink-0 w-8 h-8 rounded-full border border-[#c9a050]/30 flex items-center justify-center text-[#c9a050] transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-400 ${isOpen ? 'max-h-60 pb-5' : 'max-h-0'}`}>
        <p className="text-[#7a6b5a] leading-relaxed text-sm">{answer}</p>
      </div>
    </div>
  );
};

// Contact Popup Widget - Bottom Right
const ContactPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const formBody = new URLSearchParams({
        "form-name": "popup-contact",
        ...formData,
      }).toString();
      
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formBody,
      });
      setSubmitted(true);
      setFormData({ name: "", email: "", message: "" });
      setTimeout(() => { setSubmitted(false); setIsOpen(false); }, 3000);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-40" data-testid="contact-popup-widget">
      {/* Popup Form */}
      {isOpen && (
        <div 
          data-testid="contact-popup-form"
          className="absolute bottom-16 right-0 w-80 bg-white rounded-2xl shadow-2xl border border-[#c9a050]/20 overflow-hidden animate-fade-in-up"
        >
          <div className="bg-[#2d261f] px-5 py-4 flex items-center justify-between">
            <h3 className="font-display text-lg text-[#f8f4ef]">Quick Contact</h3>
            <button 
              onClick={() => setIsOpen(false)} 
              className="text-[#a89b8a] hover:text-[#f8f4ef] transition-colors"
              data-testid="contact-popup-close"
            >
              <X size={18} />
            </button>
          </div>
          {submitted ? (
            <div className="p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#c9a050]/10 flex items-center justify-center text-[#c9a050]">
                <Heart size={24} />
              </div>
              <p className="font-display text-lg text-[#2d261f] mb-1">Thank You!</p>
              <p className="text-[#7a6b5a] text-sm">We'll be in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-5 space-y-4" data-netlify="true" name="popup-contact">
              <input type="hidden" name="form-name" value="popup-contact" />
              <input
                type="text"
                placeholder="Your Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-[#e5ddd0] rounded-lg px-4 py-2.5 text-sm text-[#2d261f] placeholder:text-[#9b8b7a] focus:outline-none focus:border-[#c9a050] transition-colors"
                required
                data-testid="popup-contact-name"
              />
              <input
                type="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full border border-[#e5ddd0] rounded-lg px-4 py-2.5 text-sm text-[#2d261f] placeholder:text-[#9b8b7a] focus:outline-none focus:border-[#c9a050] transition-colors"
                required
                data-testid="popup-contact-email"
              />
              <textarea
                placeholder="How can we help?"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={3}
                className="w-full border border-[#e5ddd0] rounded-lg px-4 py-2.5 text-sm text-[#2d261f] placeholder:text-[#9b8b7a] focus:outline-none focus:border-[#c9a050] transition-colors resize-none"
                required
                data-testid="popup-contact-message"
              />
              <button 
                type="submit" 
                className="btn-gold w-full text-center" 
                disabled={isSubmitting}
                data-testid="popup-contact-submit"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}
        </div>
      )}

      {/* Floating Toggle Button */}
      <button
        data-testid="contact-popup-toggle"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 ${
          isOpen 
            ? 'bg-[#2d261f] text-[#c9a050] rotate-0' 
            : 'bg-gradient-to-br from-[#c9a050] to-[#a68638] text-white animate-pulse-glow hover:scale-110'
        }`}
        aria-label="Contact us"
      >
        {isOpen ? <X size={22} /> : <Mail size={22} />}
      </button>
    </div>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Main App Component
// Booking Page (Embedded)
const Booking = () => {
  return (
    <main data-testid="booking-page" className="bg-[#f8f4ef]">
      <section className="pt-28 pb-0">
        <div className="text-center px-6 pb-6">
          <p className="text-[#c9a050] text-xs tracking-[0.25em] uppercase mb-3 font-semibold">Schedule Your Visit</p>
          <h1 className="font-display text-4xl sm:text-5xl text-[#2d261f] mb-2 italic">Book an Appointment</h1>
          <p className="text-[#7a6b5a] text-sm">Select a service, choose your date, and secure your spot.</p>
        </div>
        <div className="w-full" style={{ minHeight: 'calc(100vh - 200px)' }}>
          <iframe
            data-testid="booking-embed"
            src="https://www.myaestheticspro.com/BN/index.cfm?51E47373263E1BC5CE5480E1FC65DFBD4CFC2165279C2B6FC9B29ADF9D0A6FBB"
            title="NONU Aesthetic & Wellness Online Booking"
            width="100%"
            style={{ minHeight: 'calc(100vh - 200px)', border: 0 }}
            allowFullScreen
            loading="lazy"
          />
        </div>
      </section>
    </main>
  );
};

function App() {
  return (
    <div className="App bg-[#f8f4ef] min-h-screen">
      <BrowserRouter>
        <ScrollToTop />
        <Navigation />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/treatments" element={<Treatments />} />
          <Route path="/membership" element={<Membership />} />
          <Route path="/academy" element={<Academy />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/book" element={<Booking />} />
        </Routes>
        <Footer />
        <ContactPopup />
        <Toaster 
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#2d261f',
              color: '#f8f4ef',
              border: '1px solid #b8956c',
            },
          }}
        />
      </BrowserRouter>
    </div>
  );
}

export default App;
